import Promise from 'bluebird';
import {
  MAX_MEDIA_DURATION_SECONDS,
  MEDIA_TYPE,
  TEST,
} from '../../../constants';
import MediaMetadataError from '../../../errors/MediaMetadataError';
import MediaDurationExceededError from '../../../errors/MediaDurationExceededError';
import {
  hasValidImageDimensions,
  hasValidVideoDuration,
  hasValidVideoMetadata,
} from '../../../util/mediaUtils';
import MediaLoadError from '../../../errors/MediaLoadError';
import MediaFormatError from '../../../errors/MediaFormatError';
import RetryBackoffPolicy from './RetryBackoffPolicy';
import { info } from '../../../util/log';
import { isNotNil } from '../../../util/object';
import { supportsCreateImageBitmap } from '../../../util/file';

const TEST_DATA = {
  duration: 100,
  height: 100,
  naturalHeight: 100,
  naturalWidth: 100,
  videoHeight: 100,
  videoWidth: 100,
  width: 100,
};

export default class MediaLoader {
  static INFINITE_LOAD_ATTEMPTS = -1;
  static DEFAULT_MAX_LOAD_ATTEMPTS = 5;

  _maxDuration = MAX_MEDIA_DURATION_SECONDS;
  _maxTries = MediaLoader.DEFAULT_MAX_LOAD_ATTEMPTS;
  _mediaElement = null;
  _mediaLoadPromise = null;
  _mediaLoadPromiseRejecter = null;
  _mediaLoadPromiseResolver = null;
  _mediaMetaDataPromise = null;
  _mediaMetaDataPromiseRejecter = null;
  _mediaMetaDataPromiseResolver = null;
  _mediaType = null;
  _mediaUrl = null;
  _shouldThrowErrorOnDurationExceeded = false;
  _shouldThrowErrorOnInvalidDimensions = true;
  _tries = 0;

  _currTimeoutHandler = null;

  /** @member {RetryBackoffPolicy} */
  _retryPolicy = null;

  constructor(
    mediaUrl,
    mediaType,
    retryPolicy = RetryBackoffPolicy.policyLinearBackoff(),
    useCors,
  ) {
    this._mediaUrl = mediaUrl;
    this._mediaType = mediaType;
    this._retryPolicy = retryPolicy;
    this._useCors = useCors;
  }

  createPromise() {
    let resolver;
    let rejecter;
    const promise = new Promise((resolve, reject) => {
      rejecter = reject;
      resolver = resolve;
    });

    promise.catch(() => {});
    return {
      promise,
      rejecter,
      resolver,
    };
  }

  getErrorHandler(methodToRetry) {
    return (errorEvent) => {
      if (
        this._tries < this._maxTries ||
        this._maxTries === MediaLoader.INFINITE_LOAD_ATTEMPTS
      ) {
        this.retry(methodToRetry);
      } else {
        let rejectionMessage = `Failed to load media after ${this._tries} attempts: ${errorEvent}`;
        let rejectionError = new MediaLoadError(rejectionMessage);

        if (errorEvent.target && errorEvent.target.error) {
          if (errorEvent.target.error instanceof MediaError) {
            if (
              errorEvent.target.error.code === 4 /* Format Error */ ||
              errorEvent.target.error.code === 3 /* Decode Error */
            ) {
              rejectionMessage = `Failed to load media due to a format error ${this._mediaType}`;
              rejectionError = new MediaFormatError(rejectionMessage);
            }
          }
        }
        this.rejectLoadPromises(rejectionError);
      }
    };
  }

  incrementTries() {
    this._tries = this._tries + 1;
  }

  retry(methodToRetry) {
    this.incrementTries();

    const backoffTime = this._retryPolicy.getNextBackoffTime();
    info(
      `Failed loading ${this._mediaUrl}, retrying loading in ${backoffTime} - attempt #${this._tries}`,
    );
    this._currTimeoutHandler = setTimeout(() => {
      methodToRetry();
    }, backoffTime);
  }

  // Returns a promise that resolves when the media is loaded
  // Ensure that you catch promise incase you want to do any error handling, otherwise the promise will fail silently
  loadMedia() {
    // TODO: Figure out how to mock HTMLImageElement, etc, and test it correctly.
    this.createdNeededPromises();

    if (TEST) {
      this._mediaLoadPromiseResolver(TEST_DATA);
      return this._mediaLoadPromise;
    }

    this.beginLoadingIfNeeded();

    return this._mediaLoadPromise;
  }

  // Returns a promise that resolves with metadata about the media
  // Ensure that you catch promise incase you want to do any error handling, otherwise the promise will fail silently
  loadMetaData() {
    this.createdNeededPromises();

    if (TEST) {
      this._mediaMetaDataPromiseResolver(TEST_DATA);
      return this._mediaMetaDataPromise;
    }

    this.beginLoadingIfNeeded();

    return this._mediaMetaDataPromise;
  }

  beginLoadingIfNeeded() {
    if (this._tries === 0) {
      this.incrementTries();
      // Set up promises
      switch (this._mediaType) {
        case MEDIA_TYPE.IMAGE:
        case MEDIA_TYPE.SVG:
          this.loadImage();
          break;
        case MEDIA_TYPE.VIDEO:
          this.loadVideo();
          break;
        case MEDIA_TYPE.AUDIO:
          this.loadAudio();
          break;
        default:
          throw new Error('Unrecognized media type, cannot load it');
      }
    }
  }

  createdNeededPromises() {
    if (!this._mediaLoadPromise) {
      const { promise, rejecter, resolver } = this.createPromise();
      this._mediaLoadPromise = promise;
      this._mediaLoadPromiseRejecter = rejecter;
      this._mediaLoadPromiseResolver = resolver;
    }
    if (!this._mediaMetaDataPromise) {
      const { promise, rejecter, resolver } = this.createPromise();
      this._mediaMetaDataPromise = promise;
      this._mediaMetaDataPromiseRejecter = rejecter;
      this._mediaMetaDataPromiseResolver = resolver;
    }
  }

  loadImage = () => {
    this._mediaElement = new Image();
    if (this._useCors) {
      this._mediaElement.crossOrigin = 'anonymous';
    }

    const imageErrorHandler = this.getErrorHandler(this.loadImage);

    this._mediaElement.addEventListener('load', () => {
      if (
        this._shouldThrowErrorOnInvalidDimensions &&
        !hasValidImageDimensions(this._mediaElement)
      ) {
        const imageMetaDataError = new MediaMetadataError(this._mediaElement);
        this.rejectLoadPromises(imageMetaDataError);
        this._mediaElement.removeEventListener('error', imageErrorHandler);
        return;
      }

      this._mediaMetaDataPromiseResolver({
        width: this._mediaElement.naturalWidth || this._mediaElement.width,
        height: this._mediaElement.naturalHeight || this._mediaElement.height,
        duration: null,
      });

      if (this._mediaType === MEDIA_TYPE.IMAGE && supportsCreateImageBitmap()) {
        self
          .createImageBitmap(
            this._mediaElement,
            0,
            0,
            this._mediaElement.naturalWidth,
            this._mediaElement.naturalHeight,
          )
          .then((bitmap) => {
            this._mediaLoadPromiseResolver({
              image: bitmap,
              height: this._mediaElement.naturalHeight,
              width: this._mediaElement.naturalWidth,
            });
          })
          .catch(() => {
            // For some reason in Firefox this can throw an error due to a hard to understand DOM exception
            // In that case, just use the fallback image.
            this._mediaLoadPromiseResolver({
              image: this._mediaElement,
              height: this._mediaElement.naturalHeight,
              width: this._mediaElement.naturalWidth,
            });
          });
        return;
      }

      this._mediaLoadPromiseResolver({
        image: this._mediaElement,
        height: this._mediaElement.naturalHeight,
        width: this._mediaElement.naturalWidth,
      });
    });

    this._mediaElement.addEventListener('error', imageErrorHandler);

    this._mediaElement.src = this._mediaUrl;
  };

  loadVideo = () => {
    this._mediaElement = document.createElement('video');
    if (this._useCors) {
      this._mediaElement.crossOrigin = 'anonymous';
    }
    this._mediaElement.muted = true;

    const videoErrorHandler = this.getErrorHandler(this.loadImage);
    const videoLoadedDataHandler = () => {
      this._mediaLoadPromiseResolver(this._mediaElement);
    };

    const metadataValidationFn = this._shouldThrowErrorOnInvalidDimensions
      ? hasValidVideoMetadata
      : hasValidVideoDuration;

    this._mediaElement.addEventListener('loadedmetadata', () => {
      if (!metadataValidationFn(this._mediaElement)) {
        const videoMetaDataError = new MediaMetadataError(this._mediaElement);
        this.rejectLoadPromises(videoMetaDataError);
        this._mediaElement.removeEventListener('error', videoErrorHandler);
        this._mediaElement.removeEventListener(
          'loadeddata',
          videoLoadedDataHandler,
        );
        return;
      }

      const duration = this._mediaElement.duration;

      if (
        this._shouldThrowErrorOnDurationExceeded &&
        duration > this._maxDuration
      ) {
        const videoDurationError = new MediaDurationExceededError(
          `Media cannot exceed ${this._maxDuration} seconds`,
          this._maxDuration,
        );
        this.rejectLoadPromises(videoDurationError);
        this._mediaElement.removeEventListener('error', videoErrorHandler);
        this._mediaElement.removeEventListener(
          'loadeddata',
          videoLoadedDataHandler,
        );
        return;
      }

      this._mediaMetaDataPromiseResolver({
        width: this._mediaElement.videoWidth,
        height: this._mediaElement.videoHeight,
        duration,
      });
    });

    this._mediaElement.addEventListener('loadeddata', videoLoadedDataHandler);
    this._mediaElement.addEventListener('error', videoErrorHandler);

    this._mediaElement.setAttribute('src', this._mediaUrl);
    this._mediaElement.load();
  };

  loadAudio = () => {
    this._mediaElement = document.createElement('audio');
    this._mediaElement.muted = true;

    const audioLoadedDataHandler = () =>
      this._mediaLoadPromiseResolver(this._mediaElement);
    const audioErrorHandler = this.getErrorHandler(this.loadAudio);

    this._mediaElement.addEventListener('loadedmetadata', () => {
      const duration = this._mediaElement.duration;

      if (
        this._shouldThrowErrorOnDurationExceeded &&
        duration > this._maxDuration
      ) {
        const audioDurationError = new MediaDurationExceededError(
          `Media cannot exceed ${this._maxDuration} seconds`,
          this._maxDuration,
        );
        this.rejectLoadPromises(audioDurationError);
        this._mediaElement.removeEventListener(
          'loadeddata',
          audioLoadedDataHandler,
        );
        this._mediaElement.removeEventListener('error', audioErrorHandler);
        return;
      }

      this._mediaMetaDataPromiseResolver({
        width: null,
        height: null,
        duration: Math.min(this._maxDuration, duration),
      });
    });

    this._mediaElement.addEventListener('loadeddata', audioLoadedDataHandler);
    this._mediaElement.addEventListener('error', audioErrorHandler);

    this._mediaElement.setAttribute('src', this._mediaUrl);
    this._mediaElement.load();
  };

  /**
   * @function cancel Cleanup the MediaLoader
   */
  cancel() {
    if (isNotNil(this._currTimeoutHandler)) {
      clearTimeout(this._currTimeoutHandler);
    }
    const mediaLoadError = new MediaLoadError('Cancelled Media Load.');
    this.rejectLoadPromises(mediaLoadError);
  }

  rejectLoadPromises(rejectionError) {
    if (isNotNil(this._mediaMetaDataPromiseRejecter)) {
      this._mediaMetaDataPromiseRejecter(rejectionError);
    }
    if (isNotNil(this._mediaLoadPromiseRejecter)) {
      this._mediaLoadPromiseRejecter(rejectionError);
    }
  }

  get mediaUrl() {
    return this._mediaUrl;
  }

  get mediaElement() {
    return this._mediaElement;
  }

  set maxDuration(value) {
    this._maxDuration = value;
  }

  set maxTries(value) {
    this._maxTries = value;
  }

  set shouldThrowErrorOnDurationExceeded(value) {
    this._shouldThrowErrorOnDurationExceeded = value;
  }

  set shouldThrowErrorOnInvalidDimensions(value) {
    this._shouldThrowErrorOnInvalidDimensions = value;
  }
}
